// src/hooks/useDeepLinks.js
//
// Capacitor deep-link bridge. When iOS Universal Links or Android App Links
// hand a `https://app.getscrimly.com/...` URL to the installed app, the
// `App.appUrlOpen` event fires. This hook listens for it, strips the
// hostname, and uses React Router to navigate to the correct in-app route.
//
// Without this, the OS opens the app but the WebView stays on whatever
// route was loaded last — a bad UX where push taps don't take you to the
// notified screen.
//
// Web (non-Capacitor) builds: the dynamic import resolves to a no-op since
// '@capacitor/app' isn't bundled when targeting the browser, so this hook
// safely does nothing.
//
// Required:
//   npm install @capacitor/app
//   npx cap sync

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const APP_HOST = 'app.getscrimly.com';

export default function useDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    let listenerHandle = null;
    let cancelled = false;

    (async () => {
      try {
        // Dynamic import so web builds don't need @capacitor/app installed.
        const { App } = await import('@capacitor/app');
        if (cancelled) return;

        listenerHandle = await App.addListener('appUrlOpen', (event) => {
          try {
            const url = new URL(event.url);
            // Only consume URLs on our own app host. If a coach taps
            // https://example.com/something we leave it alone.
            if (url.host !== APP_HOST) return;

            const target = `${url.pathname}${url.search}${url.hash || ''}`;
            // replace=true so the OS-launched URL doesn't add a back-stack
            // entry for the previous screen (which would be confusing).
            navigate(target || '/', { replace: true });
          } catch (err) {
            if (import.meta.env.DEV) {
              console.warn('[useDeepLinks] failed to parse', event.url, err);
            }
          }
        });

        // Cold-start case: the app was launched FROM a deep link. The
        // 'appUrlOpen' event won't fire until next time. getLaunchUrl()
        // returns the original URL on the first call after install.
        const launch = await App.getLaunchUrl();
        if (cancelled || !launch?.url) return;
        try {
          const url = new URL(launch.url);
          if (url.host === APP_HOST) {
            const target = `${url.pathname}${url.search}${url.hash || ''}`;
            navigate(target || '/', { replace: true });
          }
        } catch { /* ignore — non-URL launch */ }
      } catch (err) {
        // @capacitor/app not installed (web build) or platform unsupported.
        if (import.meta.env.DEV) {
          console.debug('[useDeepLinks] disabled (no @capacitor/app)', err?.message);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { listenerHandle?.remove?.(); } catch { /* noop */ }
    };
  }, [navigate]);
}
